<script setup lang="ts">
import {computed, ref, watch} from "vue";
import {useI18n} from "vue-i18n";
import {storeToRefs} from "pinia";
import _ from "lodash";
import moment from "moment";
import FTextField from "@/lib/components/ui/FTextField.vue";
import FCheck from "@/lib/components/ui/FCheck.vue";
import FDropdown from "@/lib/components/ui/FDropdown.vue";
import FButton from "@/lib/components/ui/FButton.vue";
import FDatePicker from "@/components/Global/FDatePicker.vue";
import SimulationFormImportItem from "@/domains/planning/components/SimulationFormImportItem.vue";
import FormSimulationsSelect from "@/domains/planning/components/FormSimulationsSelect.vue";
import MercateamLogo from "@/components/Commons/MercateamLogo.vue";
import {useMainStore} from "@/stores/mainStore";
import {useSchedulingStore} from "@/stores/schedulingStore";
import {useUserStore} from "@/lib/stores/userStore";
import {useValidateForms} from "@/composables/useValidateForms";
import {IMPORT_STATUS} from "@/domains/parameters/domains/imports/constants";
import {isArchivedSimulation} from "@/tscript/utils/simulation";
import {
  parseDate,
  periodeToText,
  isEntryOfTeam,
  IMPORT_TYPES,
  DATE_DEFAULT_FORMAT,
  type ImportIdObj,
} from "@oplit/shared-module";
import loggerHelper from "@/tscript/loggerHelper";
import {TEST_IDS} from "@/config/constants";
import {Simulation} from "@/domains/planning/types";

interface SimulationFormProps {
  isOperationOngoing?: boolean;
  isUpdate?: boolean;
  readOnly?: boolean;
}

const props = defineProps<SimulationFormProps>();

const emit = defineEmits<{
  (
    e: "handle-validate",
    payload: {
      currentSimulation: Partial<Simulation>;
      selectedImports: Record<
        (typeof IMPORT_TYPES)[keyof typeof IMPORT_TYPES],
        Partial<ImportIdObj> | Simulation
      >;
      selectedSimulation: Simulation | null;
      mercaAbsences: boolean;
    },
  );
  (e: "cancel-clicked");
}>();

const {t} = useI18n();

const {
  isScheduling,
  team,
  hasStock,
  userData,
  clientParameters,
  imports,
  simulation,
  teamSimulations,
  isDevEnv,
  hasMercateam,
  apiClient,
  simulations,
} = storeToRefs(useMainStore());
const {teamSchedulingSimulations, selectedSimulation, schedulingSimulations} =
  storeToRefs(useSchedulingStore());
const {isOplitAdmin} = storeToRefs(useUserStore());

const totalSteps = 2;
const ofsRadioFile = "importFile";
const ofsRadioSchedulingSimulation = "schedulingSimulation";
const today = moment().format(DATE_DEFAULT_FORMAT);

const simulationForm = ref<Partial<Simulation>>({
  name: "",
  forceToday: today,
  dateMin: today,
  only_admin: false,
});
const currentStep = ref<number>(1);
const currentSimulation = ref<Simulation>({});
const simulationToDuplicateEventsFrom = ref<Simulation | null>(null);
const selectedImports = ref<
  Record<
    (typeof IMPORT_TYPES)[keyof typeof IMPORT_TYPES],
    Partial<ImportIdObj> | Simulation
  >
>({});
const importTypes = ref<string[]>([]);
const importsByType = ref<{[import_type: string]: Partial<ImportIdObj>[]}>({});
const ofsImportEntryType = ref(ofsRadioFile);
const isEnabledOfsImportType = ref<boolean>(false);
const isEnabledCustomersOrdersImportType = ref<boolean>(false);
const isFloatingSimulation = ref<boolean>(false);
const shouldLoadMercaAbsences = ref<boolean>(false);
const shouldDuplicateEvents = ref<boolean>(true);

const loadImportTypesValidator = (
  importItem: Partial<ImportIdObj> | Simulation,
): boolean => {
  if (importItem) return true;
  if (isFloatingSimulation.value) return true;
  if (
    !isEnabledCustomersOrdersImportType.value &&
    !isEnabledOfsImportType.value
  )
    return false;

  return [IMPORT_TYPES.LOAD, IMPORT_TYPES.CUSTOMERS_ORDERS].some(
    (importType) => selectedImports.value[importType],
  );
};

const {errors, validateForms} = useValidateForms(
  [simulationForm, selectedImports],
  [
    {
      name: {type: "string", required: true},
      forceToday: {
        type: "string",
        validator: (date: string) => {
          if (date) return true;
          if (isFloatingSimulation.value) return true;
          if (!shouldDisplayStartDate.value) return true;
          return currentStep.value < 2 && !props.isUpdate;
        },
      },
    },
    {
      [IMPORT_TYPES.LOAD]: {
        type: "object",
        validator: (importItem) => {
          if (
            isEnabledCustomersOrdersImportType.value &&
            !isEnabledOfsImportType.value
          )
            return true;

          return loadImportTypesValidator(importItem);
        },
      },
      [IMPORT_TYPES.CUSTOMERS_ORDERS]: {
        type: "object",
        validator: (importItem) => {
          if (
            isEnabledOfsImportType.value &&
            !isEnabledCustomersOrdersImportType.value
          )
            return true;

          return loadImportTypesValidator(importItem);
        },
      },
    },
  ],
);

const title = computed<string>(() =>
  props.isUpdate
    ? t("SimulationForm.title_update")
    : t("SimulationForm.title_create"),
);
const currentStepLabel = computed<string>(() =>
  currentStep.value === 1
    ? t("SimulationForm.step_first__label")
    : t("SimulationForm.step_second__label"),
);
const canSimulationFloat = computed<boolean>(
  () =>
    team.value?.floating_simulation ??
    clientParameters.value?.floating_simulation,
);
const canSelectOfs = computed<boolean>(() =>
  hasExistingImportsForEveryType([IMPORT_TYPES.LOAD]),
);
const canSelectCustomersOrders = computed<boolean>(() => {
  if (hasStock.value) return false;
  if (isScheduling.value) return false;
  return hasExistingImportsForEveryType([
    IMPORT_TYPES.CUSTOMERS_ORDERS,
    IMPORT_TYPES.GAMMES,
  ]);
});
const canSelectComponentsInitialStock = computed<boolean>(() => {
  if (!isScheduling.value) return false;
  return hasExistingImportsForEveryType([
    IMPORT_TYPES.COMPONENTS_INITIAL_STOCK,
  ]);
});
const canSelectSchedulingSimulationAsImportFile = computed<boolean>(() => {
  if (isScheduling.value) return false;
  if (!userData.value.has_scheduling) return false;
  return schedulingSimulations.value.length > 0;
});
const shouldDisplayStartDate = computed<boolean>(() => {
  if (isFloatingSimulation.value) return false;
  return !isScheduling.value || isArchivedSimulation(currentSimulation.value);
});
const nextButtonText = computed<string>(() => {
  if (props.isUpdate) return t("Commons.update");

  return t(currentStep.value === 1 ? "Commons.next" : "Commons.create");
});
const filteredImports = computed<ImportIdObj[]>(() =>
  imports.value.filter(({client_id, status}: ImportIdObj) => {
    if (client_id !== userData.value.client_id) return false;
    if (status !== IMPORT_STATUS.INSERTED) return false;
    return true;
  }),
);
const hasNoImports = computed<boolean>(() => {
  if (!canSimulationFloat.value) return false;
  if (currentSimulation.value.forceToday) return false;
  return !currentSimulation.value.import_ids?.some(({id}) => id);
});
const simulationsList = computed(() => {
  const filteredSimulations = isScheduling.value
    ? schedulingSimulations.value
    : teamSimulations.value;
  return _.orderBy(filteredSimulations, (o) => parseDate(o.created_at), "desc");
});
const isReadOnly = computed<boolean>(
  () => isArchivedSimulation(currentSimulation.value) || props.readOnly,
);
const isDisabledFloatingSimulationCheckbox = computed<boolean>(
  () =>
    isScheduling.value &&
    props.isUpdate &&
    clientParameters.value?.unique_scheduling_simulation &&
    !isOplitAdmin.value,
);

function getSelectableImportItemsFromType(
  importType: string,
): (Partial<ImportIdObj> | Simulation)[] {
  const importsArray = (importsByType.value[importType] || []).filter(
    ({id}) => !!id,
  );

  if (importType !== IMPORT_TYPES.LOAD) return importsArray;
  if (ofsImportEntryType.value !== ofsRadioSchedulingSimulation)
    return importsArray;

  return teamSchedulingSimulations.value;
}
function handleDateChange(date: string): void {
  simulationForm.value.forceToday = date;
  simulationForm.value.dateMin = date;
}
function hasExistingImportsForAnyType(
  importTypes: (typeof IMPORT_TYPES)[keyof typeof IMPORT_TYPES][],
): boolean {
  return importTypes.some((importType) =>
    Object.keys(importsByType.value).includes(importType),
  );
}
function hasExistingImportsForEveryType(
  importTypes: (typeof IMPORT_TYPES)[keyof typeof IMPORT_TYPES][],
): boolean {
  return importTypes.every((importType) =>
    Object.keys(importsByType.value).includes(importType),
  );
}
function getImportsByType(): {[import_type: string]: Partial<ImportIdObj>[]} {
  if (!filteredImports.value?.length) return {};

  const scopedImports = isScheduling.value
    ? filteredImports.value
    : filteredImports.value.filter((filteredImport) =>
        isEntryOfTeam(filteredImport, team.value),
      );

  const sortedImports = _.orderBy(
    scopedImports,
    (o) => parseDate(o?.date),
    "desc",
  );

  const importsByType: {[import_type: string]: Partial<ImportIdObj>[]} =
    Object.fromEntries(importTypes.value.map((importType) => [importType, []]));

  for (const importType of importTypes.value) {
    const importsForCurrentType = sortedImports.filter(({data}) =>
      data?.includes(importType),
    );

    importsByType[importType] = importsForCurrentType;

    if (canSimulationFloat.value)
      importsByType[importType].unshift({id: undefined});
  }

  return importsByType;
}
function initSimulationForm() {
  currentSimulation.value = _.cloneDeep(
    isScheduling.value ? selectedSimulation.value : simulation.value,
  );

  for (const key in currentSimulation.value)
    simulationForm.value[key] = currentSimulation.value[key];

  isFloatingSimulation.value =
    (!currentSimulation.value.forceToday || isScheduling.value) &&
    hasNoImports.value &&
    canSimulationFloat.value;

  if (!currentSimulation.value.import_ids?.length) return;

  const {import_ids} = currentSimulation.value;

  for (const {id, collection, from_simu} of import_ids) {
    // currently only possible for IMPORT_TYPES.LOAD imports
    if (from_simu) {
      const existingSimulation = teamSchedulingSimulations.value?.find(
        (simulation) => simulation.id === id,
      );

      ofsImportEntryType.value = ofsRadioSchedulingSimulation;

      selectedImports.value[collection] = existingSimulation || null;
    } else {
      const existingImport = importsByType.value[collection]?.find(
        (importObject) => importObject.id === id,
      );

      selectedImports.value[collection] = existingImport || null;
    }
  }
}
function initEnabledLoadImportTypes(): void {
  if (
    props.isUpdate &&
    clientParameters.value.enable_customers_orders_imports_for_load
  ) {
    isEnabledCustomersOrdersImportType.value =
      !!selectedImports.value[IMPORT_TYPES.CUSTOMERS_ORDERS];
    isEnabledOfsImportType.value = !!selectedImports.value[IMPORT_TYPES.LOAD];
  } else {
    isEnabledOfsImportType.value = canSelectOfs.value;
    isEnabledCustomersOrdersImportType.value = canSelectCustomersOrders.value;
  }
}
function getSelectedImportItem(
  importType: string,
): Partial<ImportIdObj> | Simulation {
  const selectableImportItems = getSelectableImportItemsFromType(importType);

  if (!selectableImportItems.length) return;

  return selectableImportItems.find(({id}) => {
    const selectedImportId =
      selectedImports.value[importType]?.id ||
      selectedImports.value[importType];

    return id === selectedImportId;
  });
}
function getFormattedSimulationAndImportsForSubmission(): {
  currentSimulation: Partial<Simulation>;
  selectedImports: Record<
    (typeof IMPORT_TYPES)[keyof typeof IMPORT_TYPES],
    Partial<ImportIdObj> | Simulation
  >;
} {
  if (isFloatingSimulation.value) {
    const currentSimulation = Object.assign({}, simulationForm.value, {
      forceToday: null,
      dateMin: null,
    });

    return {
      currentSimulation,
      selectedImports: {},
    };
  } else {
    if (
      selectedImports.value[IMPORT_TYPES.LOAD] &&
      ofsImportEntryType.value === ofsRadioSchedulingSimulation
    ) {
      Object.assign(selectedImports.value[IMPORT_TYPES.LOAD], {
        from_simu: true,
      });
    }

    if (!isEnabledCustomersOrdersImportType.value)
      delete selectedImports.value[IMPORT_TYPES.CUSTOMERS_ORDERS];

    if (!isEnabledOfsImportType.value)
      delete selectedImports.value[IMPORT_TYPES.LOAD];

    return {
      currentSimulation: simulationForm.value,
      selectedImports: selectedImports.value,
    };
  }
}
function goToNextStep(): Promise<void> {
  if (props.isOperationOngoing) return;

  const isValid = validateForms();

  if (!isValid) return;

  if (currentStep.value === 2 || props.isUpdate) {
    const {currentSimulation, selectedImports} =
      getFormattedSimulationAndImportsForSubmission();

    emit("handle-validate", {
      currentSimulation,
      selectedImports,
      selectedSimulation: shouldDuplicateEvents.value
        ? simulationToDuplicateEventsFrom.value
        : null,
      mercaAbsences: hasMercateam.value && shouldLoadMercaAbsences.value,
    });
  } else if (currentStep.value === 1) currentStep.value += 1;
}
function initImportsData() {
  importTypes.value = [
    ...new Set(filteredImports.value.flatMap(({data}) => data)),
  ];
  importsByType.value = getImportsByType();
}
/**
 * this will migrate events from the selected simulation into the one in the planning store
 * warning, it doesn't seem up-to-date
 **/
async function hacky_remigrateEvents(): Promise<void> {
  if (!simulationToDuplicateEventsFrom.value?.created_at) return;

  apiClient.value
    .migrateEvents({
      simulation_id: simulation.value.id,
      simulation_start_date: simulation.value.forceToday,
      simulations: isScheduling.value
        ? schedulingSimulations.value
        : simulations.value,
      team: team.value,
      selected_simulation: simulationToDuplicateEventsFrom.value,
      client_id: userData.value.client_id,
      is_scheduling: isScheduling.value,
    })
    .then((d) => loggerHelper.log(d));
}

watch(
  imports,
  () => {
    initImportsData();
    if (props.isUpdate) initSimulationForm();
    initEnabledLoadImportTypes();
  },
  {immediate: true, deep: true},
);

/**
 * simulation is floating on every type of load imports
 */
watch(isFloatingSimulation, initEnabledLoadImportTypes);
</script>

<template>
  <div class="simulation-form text-neutral-secondary">
    <div class="simulation-form__header">
      <h4
        class="text-neutral-primary semi-bold"
        :data-testid="TEST_IDS.SIMULATION_FORM__TITLE"
      >
        {{ title }}

        <span
          v-if="team && !isScheduling"
          data-testid="simulation-form-team-title"
        >
          | {{ t("Simulation.team") }} {{ team.name }}
        </span>
      </h4>

      <span
        v-if="!isUpdate"
        class="text-neutral-tertiary"
        :data-testid="TEST_IDS.SIMULATION_FORM__STEP_DESCRIPTION"
      >
        {{ currentStep }} / {{ totalSteps }} - {{ currentStepLabel }}
      </span>
    </div>

    <template v-if="isUpdate || currentStep === 1">
      <FTextField
        v-model="simulationForm.name"
        :placeholder="t('SimulationForm.simulation_name__placeholder')"
        :outlined="['newLayerBackground']"
        :pink-border="!!errors.name"
        :disabled="isReadOnly"
        :data-testid="TEST_IDS.SIMULATION_FORM__NAME_INPUT"
      >
        <template #label>
          <label class="semi-bold d-inline-block mb-2">
            {{ t("SimulationForm.simulation_name__label") }}
          </label>
        </template>
      </FTextField>

      <FCheck
        v-if="canSimulationFloat"
        v-model="isFloatingSimulation"
        :disabled="isDisabledFloatingSimulationCheckbox"
        :label="t('SimulationForm.is_floating_simulation__label')"
        :data-testid="TEST_IDS.SIMULATION_FORM__IS_FLOATING_SIMULATION_CHECKBOX"
        no-padding
      />

      <!-- load imports-->
      <div v-if="!isFloatingSimulation" class="d-flex flex-column gap-4">
        <div class="semi-bold">
          {{ t("SimulationForm.load_imports__header") }}
        </div>

        <div
          v-if="
            clientParameters.enable_customers_orders_imports_for_load &&
            !hasStock &&
            !isScheduling
          "
          :class="{
            'mb-2':
              isEnabledOfsImportType || isEnabledCustomersOrdersImportType,
          }"
          class="fd-flex-center gap-4"
          :data-testid="
            TEST_IDS.SIMULATION_FORM__LOAD_TYPES_CHECKBOXES_CONTAINER
          "
        >
          <div
            :class="{
              'border-brand-solid bg-brand-primary text-brand-primary':
                isEnabledOfsImportType,
              'text-neutral-disabled bg-neutral-disabled': !canSelectOfs,
              'border-error-solid': errors[IMPORT_TYPES.LOAD] && canSelectOfs,
            }"
            class="flex-1 rounded-lg pa-4 shadow-neutral-xs"
          >
            <FCheck
              v-model="isEnabledOfsImportType"
              v-tooltip="{
                text: t('SimulationForm.ofs_enable__tooltip'),
                location: 'top',
                disabled: canSelectOfs,
              }"
              :disabled="!canSelectOfs || isFloatingSimulation"
              no-padding
            >
              <template #label>
                <span class="fd-flex-center gap-4">
                  <OpalIcon name="cards-three" size="28" />

                  {{ t("SimulationForm.import_type__ofs__label") }}
                </span>
              </template>
            </FCheck>
          </div>

          <div
            :class="{
              'border-brand-solid bg-brand-primary text-brand-primary':
                isEnabledCustomersOrdersImportType,
              'text-neutral-disabled bg-neutral-disabled':
                !canSelectCustomersOrders,
              'border-error-solid':
                errors[IMPORT_TYPES.CUSTOMERS_ORDERS] &&
                canSelectCustomersOrders,
            }"
            class="flex-1 rounded-lg pa-4 shadow-neutral-xs"
          >
            <FCheck
              v-model="isEnabledCustomersOrdersImportType"
              v-tooltip="{
                text: t('SimulationForm.customers_orders_enable__tooltip'),
                location: 'top',
                disabled: canSelectCustomersOrders,
              }"
              :disabled="!canSelectCustomersOrders || isFloatingSimulation"
              no-padding
            >
              <template #label>
                <span class="fd-flex-center gap-4">
                  <OpalIcon name="basket" size="28" />

                  {{ t("SimulationForm.import_type__customers_orders__label") }}
                </span>
              </template>
            </FCheck>
          </div>
        </div>

        <!-- ofs -->
        <div
          v-if="isEnabledOfsImportType && !isFloatingSimulation"
          class="d-flex flex-column gap-4"
        >
          <label>
            {{ t("SimulationForm.import_type__ofs__label") }}
          </label>

          <v-radio-group
            v-if="canSelectSchedulingSimulationAsImportFile"
            v-model="ofsImportEntryType"
            direction="horizontal"
            color="blue-500"
            class="flex-row"
            density="comfortable"
            hide-details
            @update:model-value="
              () => (selectedImports[IMPORT_TYPES.LOAD] = null)
            "
          >
            <v-radio
              :label="t('SimulationForm.ofs_file__radio')"
              :value="ofsRadioFile"
              :data-testid="TEST_IDS.SIMULATION_FORM__OFS_IMPORT_RADIO"
            />
            <v-radio
              :label="t('SimulationForm.ofs_scheduling_simulation__radio')"
              :value="ofsRadioSchedulingSimulation"
              :data-testid="
                TEST_IDS.SIMULATION_FORM__OFS_SCHEDULING_SIMULATION_RADIO
              "
            />
          </v-radio-group>

          <FDropdown
            v-model="selectedImports[IMPORT_TYPES.LOAD]"
            :items="getSelectableImportItemsFromType(IMPORT_TYPES.LOAD)"
            :pink-border="!!errors[IMPORT_TYPES.LOAD]"
            width="736"
            outlined
            return-object
            :data-testid="TEST_IDS.SIMULATION_FORM__OFS_DROPDOWN"
          >
            <template #item="{item, index}">
              <SimulationFormImportItem
                :item="item"
                :data-testid="`${TEST_IDS.SIMULATION_FORM__OFS_DROPDOWN__ITEM_PREFIX}${index}`"
              />
            </template>

            <template #content>
              <span
                v-if="!getSelectedImportItem(IMPORT_TYPES.LOAD)"
                class="text-neutral-placeholder"
              >
                {{ t("Commons.select_placeholder") }}
              </span>

              <SimulationFormImportItem
                v-else
                :item="getSelectedImportItem(IMPORT_TYPES.LOAD)"
              />
            </template>
          </FDropdown>
        </div>

        <!-- customers_orders -->
        <div
          v-if="isEnabledCustomersOrdersImportType && !isFloatingSimulation"
          class="d-flex flex-column gap-4"
        >
          <label>
            {{ t("SimulationForm.import_type__customers_orders__label") }}
          </label>

          <FDropdown
            v-model="selectedImports[IMPORT_TYPES.CUSTOMERS_ORDERS]"
            :items="
              getSelectableImportItemsFromType(IMPORT_TYPES.CUSTOMERS_ORDERS)
            "
            :pink-border="!!errors[IMPORT_TYPES.CUSTOMERS_ORDERS]"
            width="736"
            outlined
            return-object
            :data-testid="TEST_IDS.SIMULATION_FORM__CUSTOMERS_ORDERS_DROPDOWN"
          >
            <template #item="{item}">
              <SimulationFormImportItem :item="item" />
            </template>

            <template #content>
              <span
                v-if="!getSelectedImportItem(IMPORT_TYPES.CUSTOMERS_ORDERS)"
                class="text-neutral-placeholder"
              >
                {{ t("Commons.select_placeholder") }}
              </span>

              <SimulationFormImportItem
                v-else
                :item="getSelectedImportItem(IMPORT_TYPES.CUSTOMERS_ORDERS)"
              />
            </template>
          </FDropdown>
        </div>
      </div>

      <!-- stock imports -->
      <div
        v-if="
          !isFloatingSimulation &&
          hasExistingImportsForAnyType([
            IMPORT_TYPES.STOCK,
            IMPORT_TYPES.STOCK_PF,
            IMPORT_TYPES.COMPONENTS_INITIAL_STOCK,
          ])
        "
        class="d-flex flex-column gap-4"
      >
        <div class="semi-bold">
          {{ t("SimulationForm.stocks_imports__header") }}
        </div>

        <!-- initial_stock -->
        <div
          v-if="hasExistingImportsForEveryType([IMPORT_TYPES.STOCK])"
          class="d-flex flex-column gap-4"
        >
          <label>
            {{ t("SimulationForm.import_type__initial_stock__label") }}
          </label>

          <FDropdown
            v-model="selectedImports[IMPORT_TYPES.STOCK]"
            :items="getSelectableImportItemsFromType(IMPORT_TYPES.STOCK)"
            width="736"
            outlined
            return-object
            :data-testid="TEST_IDS.SIMULATION_FORM__STOCK_DROPDOWN"
          >
            <template #item="{item, index}">
              <SimulationFormImportItem
                :item="item"
                :data-testid="`${TEST_IDS.SIMULATION_FORM__STOCK_DROPDOWN__ITEM_PREFIX}${index}`"
              />
            </template>

            <template #content>
              <span
                v-if="!getSelectedImportItem(IMPORT_TYPES.STOCK)"
                class="text-neutral-placeholder"
              >
                {{ t("Commons.select_placeholder") }}
              </span>

              <SimulationFormImportItem
                v-else
                :item="getSelectedImportItem(IMPORT_TYPES.STOCK)"
              />
            </template>
          </FDropdown>
        </div>

        <!-- initial_stock_pf -->
        <div
          v-if="hasExistingImportsForEveryType([IMPORT_TYPES.STOCK_PF])"
          class="d-flex flex-column gap-4"
        >
          <label>
            {{ t("SimulationForm.import_type__initial_stock_pf__label") }}
          </label>

          <FDropdown
            v-model="selectedImports[IMPORT_TYPES.STOCK_PF]"
            :items="getSelectableImportItemsFromType(IMPORT_TYPES.STOCK_PF)"
            width="736"
            outlined
            return-object
            :data-testid="TEST_IDS.SIMULATION_FORM__STOCK_PF_DROPDOWN"
          >
            <template #item="{item, index}">
              <SimulationFormImportItem
                :item="item"
                :data-testid="`${TEST_IDS.SIMULATION_FORM__STOCK_PF_DROPDOWN__ITEM_PREFIX}${index}`"
              />
            </template>

            <template #content>
              <span
                v-if="!getSelectedImportItem(IMPORT_TYPES.STOCK_PF)"
                class="text-neutral-placeholder"
              >
                {{ t("Commons.select_placeholder") }}
              </span>

              <SimulationFormImportItem
                v-else
                :item="getSelectedImportItem(IMPORT_TYPES.STOCK_PF)"
              />
            </template>
          </FDropdown>
        </div>

        <!-- components_initial_stock -->
        <div
          v-if="canSelectComponentsInitialStock"
          class="d-flex flex-column gap-4"
        >
          <label>
            {{
              t("SimulationForm.import_type__components_initial_stock__label")
            }}
          </label>

          <FDropdown
            v-model="selectedImports[IMPORT_TYPES.COMPONENTS_INITIAL_STOCK]"
            :items="
              getSelectableImportItemsFromType(
                IMPORT_TYPES.COMPONENTS_INITIAL_STOCK,
              )
            "
            width="736"
            outlined
            return-object
            :data-testid="
              TEST_IDS.SIMULATION_FORM__COMPONENTS_INITIAL_STOCK_DROPDOWN
            "
          >
            <template #item="{item}">
              <SimulationFormImportItem :item="item" />
            </template>

            <template #content>
              <span
                v-if="
                  !getSelectedImportItem(IMPORT_TYPES.COMPONENTS_INITIAL_STOCK)
                "
                class="text-neutral-placeholder"
              >
                {{ t("Commons.select_placeholder") }}
              </span>

              <SimulationFormImportItem
                v-else
                :item="
                  getSelectedImportItem(IMPORT_TYPES.COMPONENTS_INITIAL_STOCK)
                "
              />
            </template>
          </FDropdown>
        </div>
      </div>
    </template>

    <template v-if="isUpdate || currentStep === 2">
      <div v-if="!isUpdate" class="d-flex flex-column gap-4">
        <label
          class="semi-bold"
          :data-testid="TEST_IDS.SIMULATION_FORM__DUPLICATE_EVENTS_LABEL"
        >
          {{ t("Simulation.migrate_events") }}
        </label>

        <v-radio-group
          v-model="shouldDuplicateEvents"
          :disabled="isUpdate"
          direction="horizontal"
          color="blue-500"
          class="flex-row"
          density="comfortable"
          hide-details
        >
          <v-radio :label="t('global.yes')" :value="true" />
          <v-radio
            :label="t('global.no')"
            :value="false"
            :data-testid="
              TEST_IDS.SIMULATION_FORM__DUPLICATE_EVENTS_FALSE_RADIO
            "
          />
        </v-radio-group>

        <FormSimulationsSelect
          v-if="shouldDuplicateEvents"
          :simulations="simulationsList"
          hide-default-option
          @remigrate-events="hacky_remigrateEvents"
          @update-selected-simulation="
            (simulation) => (simulationToDuplicateEventsFrom = simulation)
          "
        />
      </div>

      <div v-if="shouldDisplayStartDate" class="d-flex flex-column gap-4">
        <label class="semi-bold">
          {{ t("SimulationForm.force_today__label") }}
        </label>

        <FDatePicker
          :disabled="isReadOnly"
          :placeholder="t('Simulation.enter_a_date')"
          :date-value="
            simulationForm.forceToday
              ? periodeToText(simulationForm.forceToday)
              : ''
          "
          :field-value="simulationForm.forceToday"
          :handle-change="handleDateChange"
          :field-class="{'border-error-solid': errors.forceToday}"
          class="w-100"
          outlined
          btn-display
          :data-testid="TEST_IDS.SIMULATION_FORM__DATEPICKER"
        />

        <FCheck
          v-if="isDevEnv || userData.oplit_bugbug_user"
          :model-value="simulationForm.forceToday === '2023-01-01'"
          data-testid="force-today-checkbox"
          label="(dev - Bugbug) Forcer la définition de la date de début de la simulation au 01/01/2023"
          @update:model-value="
            (bool) =>
              handleDateChange(
                bool ? '2023-01-01' : moment().format(DATE_DEFAULT_FORMAT),
              )
          "
        />
      </div>

      <div
        class="fd-flex-center justify-space-between"
        v-if="!isUpdate && hasMercateam && !isScheduling"
      >
        <FCheck
          v-model="shouldLoadMercaAbsences"
          id="merca-checkbox"
          :label="t('SimulationForm.should_load_merca_absences__label')"
          no-padding
          :data-testid="TEST_IDS.SIMULATION_FORM__MERCA_ABSENCES_CHECKBOX"
        />

        <MercateamLogo :height="30" small />
      </div>

      <template v-if="isOplitAdmin">
        <FCheck
          v-model="simulationForm.only_admin"
          id="only_admin-checkbox"
          :label="t('SimulationForm.only_admin__label')"
          :data-testid="TEST_IDS.SIMULATION_FORM__ONLY_ADMIN"
        />
      </template>
    </template>

    <div class="fd-flex-center justify-end gap-4">
      <div v-if="currentStep === 2" class="flex-1">
        <FButton
          outlined
          :data-testid="TEST_IDS.SIMULATION_FORM__BACK_BUTTON"
          @click="() => (currentStep = 1)"
        >
          {{ t("global.back") }}
        </FButton>
      </div>

      <FButton
        outlined
        :data-testid="TEST_IDS.SIMULATION_FORM__CANCEL_BUTTON"
        @click="() => emit('cancel-clicked')"
      >
        {{ t("global.cancel") }}
      </FButton>

      <FButton
        :loading="isOperationOngoing"
        filled
        :data-testid="TEST_IDS.SIMULATION_FORM__NEXT_BUTTON"
        @click="() => goToNextStep()"
      >
        {{ nextButtonText }}
      </FButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
.simulation-form {
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 32px;
}

:deep() {
  .v-selection-control-group {
    flex-direction: row;
    gap: 24px;

    & .v-selection-control {
      --v-selection-control-size: 24px;
      flex: 0 0 auto;
      gap: 8px;

      & label {
        font-size: 14px;
      }
    }
  }

  .mx-input-wrapper {
    width: 100%;

    & > button {
      flex: 1;
    }
  }
}
</style>
